// components/ui/Badge.tsx
// UI Phase 1 primitive: status pill. One color contract for the whole app —
// the same status scale used by progress bars, gates, and chat action chips.

import type { HTMLAttributes } from 'react';
import { cn } from './cn';

export type BadgeStatus =
  | 'completed'
  | 'progress'
  | 'planned'
  | 'suggested'
  | 'warning'
  | 'critical'
  | 'neutral';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  status?: BadgeStatus;
}

// Soft tint background from the base status token + text from its -fg tier
// (measured ≥4.5:1 on the tint — the vivid bases are fills, not text colors),
// so a theme (or institution) swap recolors every badge from one place.
const STATUS_CLASSES: Record<BadgeStatus, string> = {
  completed: 'bg-status-completed/15 text-status-completed-fg',
  progress: 'bg-status-progress/12 text-status-progress-fg',
  planned: 'bg-status-planned/15 text-status-planned-fg',
  suggested: 'bg-status-suggested/12 text-status-suggested-fg',
  warning: 'bg-status-warning/12 text-status-warning-fg',
  critical: 'bg-status-critical/10 text-status-critical-fg',
  neutral: 'bg-surface-sunken text-ink-2',
};

const Badge = ({ status = 'neutral', className, children, ...rest }: BadgeProps) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap',
      STATUS_CLASSES[status],
      className
    )}
    {...rest}
  >
    {children}
  </span>
);

export default Badge;
